export enum ReachBrowser {
	REACH = 'REACH',
	CHROME = 'CHROME',
	FIREFOX = 'FIREFOX',
	SAFARI = 'SAFARI',
	EDGE = 'EDGE',
	BRAVE = 'BRAVE',
	OPERA = 'OPERA',
}

export interface ReachBrowserItem {
	id: ReachBrowser;
	name: string;
	imageUrl: string;
}

export const reachBrowserName: Record<ReachBrowser, string> = {
	[ReachBrowser.REACH]: 'myReach',
	[ReachBrowser.CHROME]: 'Chrome',
	[ReachBrowser.FIREFOX]: 'Firefox',
	[ReachBrowser.SAFARI]: 'Safari',
	[ReachBrowser.EDGE]: 'Edge',
	[ReachBrowser.BRAVE]: 'Brave',
	[ReachBrowser.OPERA]: 'Opera',
};

export const reachBrowserImageUrl: Record<ReachBrowser, string> = {
	[ReachBrowser.REACH]: 'assets/images/logo/logo.png',
	[ReachBrowser.CHROME]: 'assets/images/browsers/chrome.png',
	[ReachBrowser.FIREFOX]: 'assets/images/browsers/firefox.png',
	[ReachBrowser.SAFARI]: 'assets/images/browsers/safari.png',
	[ReachBrowser.EDGE]: 'assets/images/browsers/edge.png',
	[ReachBrowser.BRAVE]: 'assets/images/browsers/brave.png',
	[ReachBrowser.OPERA]: 'assets/images/browsers/opera.png',
};

type CustomWindowType = typeof window & {
	MSStream?: boolean;
	safari?: {
		pushNotification?: boolean;
	};
	opera?: string | { addons?: boolean };
	opr?: {
		addons?: boolean;
	};
	InstallTrigger?: boolean;
	isIE?: boolean;
	StyleMedia?: boolean;
	brave?: {
		isBrave: {
			name: string;
		};
	};
	navigator: {
		brave?: {
			isBrave: {
				name: string;
			};
		};
	};
};

export function mobilePlatform(): 'android' | 'ios' | 'unknown' {
	const userAgent = navigator.userAgent || navigator.vendor || (window as CustomWindowType).opera;

	if (userAgent && typeof userAgent === 'string' && /android/i.test(userAgent)) {
		return 'android';
	}

	// iOS detection from: http://stackoverflow.com/a/9039885/177710
	if (
		userAgent &&
		typeof userAgent === 'string' &&
		/iPad|iPhone|iPod/.test(userAgent) &&
		!(window as CustomWindowType).MSStream
	) {
		return 'ios';
	}

	return 'unknown';
}

export function isMobile(): boolean {
	// device detection
	if (
		/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
			navigator.userAgent
		) ||
		/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
			navigator.userAgent.substring(0, 4)
		)
	) {
		return true;
	}
	return false;
}

export function detectBrowser(): ReachBrowser {
	const anyWindow = window as CustomWindowType;

	// Opera 8.0+
	const isOpera =
		(!!anyWindow.opr && !!anyWindow.opr.addons) ||
		!!anyWindow.opera ||
		navigator.userAgent.includes(' OPR/');

	if (isOpera) {
		return ReachBrowser.OPERA;
	}

	// Firefox 1.0+
	const isFirefox = typeof anyWindow.InstallTrigger !== 'undefined';

	if (isFirefox) {
		return ReachBrowser.FIREFOX;
	}

	// Safari 3.0+ "[object HTMLElementConstructor]"
	const isSafari =
		/constructor/i.test(anyWindow.HTMLElement.toString()) ||
		(function (p) {
			return p?.toString() === '[object SafariRemoteNotification]';
		})(
			!(window as CustomWindowType).safari ||
				(typeof anyWindow.safari !== 'undefined' &&
					(window as CustomWindowType).safari?.pushNotification)
		);

	const isMobileSafari = !!/Version\/[\d\.]+.*Safari/.exec(window.navigator.userAgent);

	if (isSafari || isMobileSafari) {
		return ReachBrowser.SAFARI;
	}

	// Edge 20+
	const isEdge = !anyWindow.isIE && !!anyWindow.StyleMedia;

	if (isEdge) {
		return ReachBrowser.EDGE;
	}

	const isBrave = anyWindow.navigator.brave?.isBrave.name == 'isBrave';

	if (isBrave) {
		return ReachBrowser.BRAVE;
	}

	return ReachBrowser.CHROME;
}
